import type {
  AuditEvent,
  CustomerMembership,
  InventoryTransfer,
  InventoryTransferHistory,
  MembershipBenefit,
  MembershipBillingCycle,
  MembershipCharge,
  MembershipFulfillment,
  MembershipPlan,
  PaymentMethod,
  Phase8Snapshot,
  ProductWithInventory,
} from '../../types/domain';
import { applyInventoryBatch, getCatalog, notifyCatalogChanged } from '../catalog/catalog-store';
import { getOpenCashSession, registerCashMovement } from '../sales/sales-store';

export const PHASE8_DATABASE_KEY = 'nexo.phase8.analytics.database';
export const PHASE8_CHANGE_EVENT = 'nexo:phase8-changed';

type Phase8Database = { version: 1; businesses: Record<string, Phase8Snapshot> };
const emptyDatabase = (): Phase8Database => ({ version: 1, businesses: {} });
const id = (prefix: string) => `${prefix}_${crypto.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`}`;
const nowIso = () => new Date().toISOString();
const today = () => new Date().toISOString().slice(0, 10);

const addDays = (value: string, days: number) => {
  const date = new Date(`${value}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const addCycle = (value: string, cycle: MembershipBillingCycle, interval: number) => {
  const date = new Date(`${value}T12:00:00Z`);
  if (cycle === 'weekly') date.setUTCDate(date.getUTCDate() + 7 * interval);
  else if (cycle === 'monthly') date.setUTCMonth(date.getUTCMonth() + interval);
  else if (cycle === 'quarterly') date.setUTCMonth(date.getUTCMonth() + 3 * interval);
  else date.setUTCFullYear(date.getUTCFullYear() + interval);
  return date.toISOString().slice(0, 10);
};

const readDatabase = (): Phase8Database => {
  const raw = localStorage.getItem(PHASE8_DATABASE_KEY);
  if (!raw) return emptyDatabase();
  try {
    const parsed = JSON.parse(raw) as Phase8Database;
    return parsed.version === 1 && parsed.businesses ? parsed : emptyDatabase();
  } catch {
    return emptyDatabase();
  }
};

const writeDatabase = (database: Phase8Database) => {
  localStorage.setItem(PHASE8_DATABASE_KEY, JSON.stringify(database));
  window.dispatchEvent(new CustomEvent(PHASE8_CHANGE_EVENT));
};

const seedPlan = (businessId: string): MembershipPlan[] => {
  const timestamp = nowIso();
  if (businessId === 'biz_casa_norte') {
    const planId = 'mplan_cafe_demo';
    return [{
      id: planId,
      businessId,
      name: 'Club Café Mensual',
      description: 'Ocho bebidas al mes y precio preferente en alimentos.',
      priceCents: 69900,
      billingCycle: 'monthly',
      billingInterval: 1,
      active: true,
      graceDays: 5,
      benefits: [{ id: 'mbenefit_cafe_demo', planId, label: 'Bebida incluida', quantity: 8, productId: 'prod_americano', allowCarryOver: false, description: 'Café americano o bebida equivalente.' }],
      createdAt: timestamp,
      updatedAt: timestamp,
    }];
  }
  if (businessId === 'biz_taller_norte') {
    const planId = 'mplan_service_demo';
    return [{ id: planId, businessId, name: 'Mantenimiento mensual', description: 'Dos revisiones preventivas cada mes.', priceCents: 99000, billingCycle: 'monthly', billingInterval: 1, active: true, graceDays: 5, benefits: [{ id: 'mbenefit_service_demo', planId, label: 'Revisión preventiva', quantity: 2, allowCarryOver: false, description: 'Servicio programado en zona.' }], createdAt: timestamp, updatedAt: timestamp }];
  }
  if (businessId === 'biz_venta_movil') {
    const planId = 'mplan_retail_demo';
    return [{ id: planId, businessId, name: 'Club de novedades', description: 'Un artículo seleccionado y acceso anticipado cada mes.', priceCents: 49900, billingCycle: 'monthly', billingInterval: 1, active: true, graceDays: 5, benefits: [{ id: 'mbenefit_retail_demo', planId, label: 'Artículo mensual', quantity: 1, allowCarryOver: false, description: 'Producto seleccionado por el negocio.' }], createdAt: timestamp, updatedAt: timestamp }];
  }
  return [];
};

const emptySnapshot = (businessId: string): Phase8Snapshot => ({
  businessId,
  transfers: [],
  transferHistory: [],
  membershipPlans: seedPlan(businessId),
  memberships: [],
  membershipCharges: [],
  membershipFulfillments: [],
  auditEvents: [],
});

export const ensurePhase8 = (businessId: string) => {
  const database = readDatabase();
  if (!database.businesses[businessId]) {
    database.businesses[businessId] = emptySnapshot(businessId);
    writeDatabase(database);
  }
  return database.businesses[businessId];
};

export const getPhase8 = (businessId: string) => readDatabase().businesses[businessId] ?? null;
export const subscribePhase8 = (listener: () => void) => {
  window.addEventListener(PHASE8_CHANGE_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => { window.removeEventListener(PHASE8_CHANGE_EVENT, listener); window.removeEventListener('storage', listener); };
};

const updateBusiness = (businessId: string, updater: (snapshot: Phase8Snapshot) => Phase8Snapshot) => {
  const database = readDatabase();
  const current = database.businesses[businessId] ?? emptySnapshot(businessId);
  database.businesses[businessId] = updater(current);
  writeDatabase(database);
  return database.businesses[businessId];
};

export const recordAudit = (businessId: string, input: Omit<AuditEvent, 'id' | 'businessId' | 'createdAt'>) => {
  const event: AuditEvent = { ...input, id: id('aud'), businessId, createdAt: nowIso() };
  updateBusiness(businessId, (snapshot) => ({ ...snapshot, auditEvents: [event, ...snapshot.auditEvents].slice(0, 1000) }));
  return event;
};

const transferHistory = (transferId: string, fromStatus: InventoryTransfer['status'] | undefined, toStatus: InventoryTransfer['status'], comment: string, createdBy: string): InventoryTransferHistory => ({
  id: id('trh'), transferId, fromStatus, toStatus, comment, createdBy, createdAt: nowIso(),
});

const nextTransferFolio = (snapshot: Phase8Snapshot) => `TR-${String(snapshot.transfers.length + 1).padStart(6, '0')}`;
export const createInventoryTransfer = (businessId: string, input: {
  sourceBranchId: string; destinationBranchId: string; notes?: string; requestedBy: string; items: { product: ProductWithInventory; quantity: number; variantId?: string }[];
}) => {
  if (!input.sourceBranchId || !input.destinationBranchId || input.sourceBranchId === input.destinationBranchId) throw new Error('Selecciona dos puntos operativos diferentes.');
  if (!input.items.length) throw new Error('Agrega al menos un producto a la transferencia.');
  let created!: InventoryTransfer;
  updateBusiness(businessId, (snapshot) => {
    const transferId = id('trf');
    created = {
      id: transferId,
      folio: nextTransferFolio(snapshot),
      businessId,
      sourceBranchId: input.sourceBranchId,
      destinationBranchId: input.destinationBranchId,
      status: 'requested',
      notes: input.notes?.trim() ?? '',
      requestedBy: input.requestedBy,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      items: input.items.map((item) => ({ id: id('tri'), transferId, productId: item.product.id, variantId: item.variantId, name: item.product.name, sku: item.product.sku, requestedQuantity: item.quantity, sentQuantity: 0, receivedQuantity: 0 })),
    };
    return { ...snapshot, transfers: [created, ...snapshot.transfers], transferHistory: [transferHistory(transferId, undefined, 'requested', 'Transferencia solicitada.', input.requestedBy), ...snapshot.transferHistory] };
  });
  recordAudit(businessId, { branchId: input.sourceBranchId, userId: input.requestedBy, entityType: 'transfer', entityId: created.id, action: 'transfer.requested', summary: `${created.folio} solicitada con ${created.items.length} artículos.`, metadata: { destinationBranchId: input.destinationBranchId } });
  return created;
};

const updateTransferStatus = (businessId: string, transferId: string, status: InventoryTransfer['status'], userId: string, comment: string, patch: Partial<InventoryTransfer> = {}) => {
  let updated!: InventoryTransfer;
  updateBusiness(businessId, (snapshot) => {
    const current = snapshot.transfers.find((item) => item.id === transferId);
    if (!current) throw new Error('No se encontró la transferencia.');
    updated = { ...current, ...patch, status, updatedAt: nowIso() };
    return { ...snapshot, transfers: snapshot.transfers.map((item) => item.id === transferId ? updated : item), transferHistory: [transferHistory(transferId, current.status, status, comment, userId), ...snapshot.transferHistory] };
  });
  recordAudit(businessId, { branchId: updated.sourceBranchId, userId, entityType: 'transfer', entityId: transferId, action: `transfer.${status}`, summary: `${updated.folio}: ${comment}`, metadata: { status } });
  return updated;
};

export const approveInventoryTransfer = (businessId: string, transferId: string, userId: string) => {
  const transfer = getPhase8(businessId)?.transfers.find((item) => item.id === transferId);
  if (!transfer || transfer.status !== 'requested') throw new Error('Solo pueden aprobarse transferencias solicitadas.');
  const catalog = getCatalog(businessId);
  if (!catalog) throw new Error('No se encontró el inventario.');
  for (const item of transfer.items) {
    const row = catalog.inventory.find((inventory) => inventory.branchId === transfer.sourceBranchId && inventory.productId === item.productId);
    const available = (row?.onHand ?? 0) - (row?.reserved ?? 0);
    if (available < item.requestedQuantity) throw new Error(`${item.name} no tiene existencia suficiente en el origen.`);
  }
  return updateTransferStatus(businessId, transferId, 'approved', userId, 'Transferencia aprobada.', { approvedBy: userId });
};

export const prepareInventoryTransfer = (businessId: string, transferId: string, userId: string) => {
  const transfer = getPhase8(businessId)?.transfers.find((item) => item.id === transferId);
  if (!transfer || !['approved', 'preparing'].includes(transfer.status)) throw new Error('La transferencia debe estar aprobada.');
  return updateTransferStatus(businessId, transferId, 'preparing', userId, 'Mercancía preparada para envío.', { preparedBy: userId });
};

export const shipInventoryTransfer = (businessId: string, transferId: string, userId: string) => {
  const transfer = getPhase8(businessId)?.transfers.find((item) => item.id === transferId);
  if (!transfer || transfer.status !== 'preparing') throw new Error('Primero prepara la transferencia.');
  applyInventoryBatch(businessId, transfer.items.flatMap((item) => [
    { productId: item.productId, branchId: transfer.sourceBranchId, onHandDelta: -item.requestedQuantity, type: 'transfer-out' as const, reason: `Envío ${transfer.folio}`, reference: transfer.folio, createdBy: userId },
    { productId: item.productId, branchId: transfer.destinationBranchId, inTransitDelta: item.requestedQuantity, type: 'transfer-in' as const, reason: `En tránsito ${transfer.folio}`, reference: transfer.folio, createdBy: userId },
  ]));
  return updateTransferStatus(businessId, transferId, 'in-transit', userId, 'Mercancía enviada y marcada en tránsito.', { shippedBy: userId, shippedAt: nowIso(), items: transfer.items.map((item) => ({ ...item, sentQuantity: item.requestedQuantity })) });
};

export const receiveInventoryTransfer = (businessId: string, transferId: string, quantities: Record<string, number>, userId: string) => {
  const transfer = getPhase8(businessId)?.transfers.find((item) => item.id === transferId);
  if (!transfer || transfer.status !== 'in-transit') throw new Error('La transferencia no está en tránsito.');
  const receivedItems = transfer.items.map((item) => {
    const quantity = quantities[item.id] ?? item.sentQuantity;
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > item.sentQuantity) throw new Error(`La recepción de ${item.name} no es válida.`);
    return { ...item, receivedQuantity: quantity };
  });
  applyInventoryBatch(businessId, receivedItems.map((item) => ({ productId: item.productId, branchId: transfer.destinationBranchId, onHandDelta: item.receivedQuantity, inTransitDelta: -item.sentQuantity, type: 'transfer-in' as const, reason: `Recepción ${transfer.folio}`, reference: transfer.folio, createdBy: userId })));
  const difference = receivedItems.some((item) => item.receivedQuantity !== item.sentQuantity);
  return updateTransferStatus(businessId, transferId, difference ? 'received-with-difference' : 'received', userId, difference ? 'Transferencia recibida con diferencias.' : 'Transferencia recibida completa.', { receivedBy: userId, receivedAt: nowIso(), items: receivedItems });
};

export const cancelInventoryTransfer = (businessId: string, transferId: string, userId: string, reason: string) => {
  const transfer = getPhase8(businessId)?.transfers.find((item) => item.id === transferId);
  if (!transfer || ['in-transit', 'received', 'received-with-difference'].includes(transfer.status)) throw new Error('Una transferencia enviada no puede cancelarse sin recepción.');
  return updateTransferStatus(businessId, transferId, 'cancelled', userId, reason.trim() || 'Transferencia cancelada.');
};

export type MembershipPlanInput = { id?: string; name: string; description: string; priceCents: number; billingCycle: MembershipBillingCycle; billingInterval: number; graceDays: number; active: boolean; benefits: { id?: string; label: string; quantity: number; productId?: string; variantId?: string; allowCarryOver: boolean; description: string }[] };
export const saveMembershipPlan = (businessId: string, input: MembershipPlanInput, userId: string) => {
  if (!input.name.trim()) throw new Error('La membresía necesita un nombre.');
  if (input.priceCents < 0) throw new Error('El precio no puede ser negativo.');
  if (!input.benefits.length || input.benefits.some((benefit) => !benefit.label.trim() || benefit.quantity <= 0)) throw new Error('Agrega al menos un beneficio válido.');
  let saved!: MembershipPlan;
  updateBusiness(businessId, (snapshot) => {
    const timestamp = nowIso();
    const planId = input.id ?? id('mplan');
    const existing = snapshot.membershipPlans.find((plan) => plan.id === planId);
    saved = {
      id: planId, businessId, name: input.name.trim(), description: input.description.trim(), priceCents: input.priceCents, billingCycle: input.billingCycle,
      billingInterval: Math.max(1, Math.round(input.billingInterval)), active: input.active, graceDays: Math.max(0, Math.round(input.graceDays)),
      benefits: input.benefits.map((benefit) => ({ id: benefit.id ?? id('mbenefit'), planId, label: benefit.label.trim(), quantity: Math.max(1, Math.round(benefit.quantity)), productId: benefit.productId, variantId: benefit.variantId, allowCarryOver: benefit.allowCarryOver, description: benefit.description.trim() })),
      createdAt: existing?.createdAt ?? timestamp, updatedAt: timestamp,
    };
    return { ...snapshot, membershipPlans: existing ? snapshot.membershipPlans.map((plan) => plan.id === planId ? saved : plan) : [saved, ...snapshot.membershipPlans] };
  });
  recordAudit(businessId, { userId, entityType: 'membership', entityId: saved.id, action: input.id ? 'membership.plan.updated' : 'membership.plan.created', summary: `${saved.name} guardada.`, metadata: { priceCents: saved.priceCents, cycle: saved.billingCycle } });
  return saved;
};

const benefitsBalance = (plan: MembershipPlan, previous?: Record<string, number>) => Object.fromEntries(plan.benefits.map((benefit) => [benefit.id, benefit.quantity + (benefit.allowCarryOver ? previous?.[benefit.id] ?? 0 : 0)]));
const nextChargeFolio = (snapshot: Phase8Snapshot) => `MEM-${String(snapshot.membershipCharges.length + 1).padStart(6, '0')}`;
export const enrollCustomerMembership = (businessId: string, input: { customerId: string; planId: string; startDate: string; notes?: string; userId: string }) => {
  let membership!: CustomerMembership;
  let charge!: MembershipCharge;
  updateBusiness(businessId, (snapshot) => {
    const plan = snapshot.membershipPlans.find((item) => item.id === input.planId && item.active);
    if (!plan) throw new Error('Selecciona una membresía activa.');
    if (snapshot.memberships.some((item) => item.customerId === input.customerId && item.planId === input.planId && ['active', 'past-due', 'paused'].includes(item.status))) throw new Error('El cliente ya tiene esta membresía.');
    const periodEnd = addCycle(input.startDate, plan.billingCycle, plan.billingInterval);
    const membershipId = id('cmem');
    membership = { id: membershipId, businessId, customerId: input.customerId, planId: plan.id, status: 'active', startDate: input.startDate, currentPeriodStart: input.startDate, currentPeriodEnd: periodEnd, nextBillingDate: periodEnd, remainingBenefits: benefitsBalance(plan), notes: input.notes?.trim() ?? '', createdAt: nowIso(), updatedAt: nowIso() };
    charge = { id: id('mcharge'), folio: nextChargeFolio(snapshot), businessId, membershipId, customerId: input.customerId, periodStart: input.startDate, periodEnd, dueDate: addDays(input.startDate, plan.graceDays), amountCents: plan.priceCents, status: plan.priceCents === 0 ? 'paid' : 'pending', paidCents: plan.priceCents === 0 ? plan.priceCents : 0, createdAt: nowIso(), paidAt: plan.priceCents === 0 ? nowIso() : undefined };
    return { ...snapshot, memberships: [membership, ...snapshot.memberships], membershipCharges: [charge, ...snapshot.membershipCharges] };
  });
  recordAudit(businessId, { userId: input.userId, entityType: 'membership', entityId: membership.id, action: 'membership.enrolled', summary: `Cliente inscrito en membresía.`, metadata: { planId: input.planId, customerId: input.customerId } });
  return { membership, charge };
};

export const processMembershipRenewals = (businessId: string) => {
  const date = today();
  return updateBusiness(businessId, (snapshot) => {
    let changed = false;
    let charges = [...snapshot.membershipCharges];
    const memberships = snapshot.memberships.map((membership) => {
      if (membership.status !== 'active' || membership.nextBillingDate > date) return membership;
      const plan = snapshot.membershipPlans.find((item) => item.id === membership.planId);
      if (!plan) return membership;
      let current = { ...membership };
      let safety = 0;
      while (current.nextBillingDate <= date && safety < 24) {
        const periodStart = current.nextBillingDate;
        const periodEnd = addCycle(periodStart, plan.billingCycle, plan.billingInterval);
        if (!charges.some((charge) => charge.membershipId === current.id && charge.periodStart === periodStart)) {
          charges = [{ id: id('mcharge'), folio: `MEM-${String(charges.length + 1).padStart(6, '0')}`, businessId, membershipId: current.id, customerId: current.customerId, periodStart, periodEnd, dueDate: addDays(periodStart, plan.graceDays), amountCents: plan.priceCents, status: plan.priceCents === 0 ? 'paid' : 'pending', paidCents: plan.priceCents === 0 ? plan.priceCents : 0, createdAt: nowIso(), paidAt: plan.priceCents === 0 ? nowIso() : undefined }, ...charges];
        }
        current = { ...current, currentPeriodStart: periodStart, currentPeriodEnd: periodEnd, nextBillingDate: periodEnd, remainingBenefits: benefitsBalance(plan, current.remainingBenefits), updatedAt: nowIso() };
        changed = true; safety += 1;
      }
      return current;
    });
    const updatedCharges = charges.map((charge) => charge.status === 'pending' && charge.dueDate < date ? { ...charge, status: 'overdue' as const } : charge);
    const overdueMembershipIds = new Set(updatedCharges.filter((charge) => charge.status === 'overdue').map((charge) => charge.membershipId));
    const updatedMemberships = memberships.map((membership) => overdueMembershipIds.has(membership.id) && membership.status === 'active' ? { ...membership, status: 'past-due' as const, updatedAt: nowIso() } : membership);
    if (!changed && JSON.stringify(updatedCharges) === JSON.stringify(snapshot.membershipCharges)) return snapshot;
    return { ...snapshot, memberships: updatedMemberships, membershipCharges: updatedCharges };
  });
};

export const recordMembershipPayment = (businessId: string, chargeId: string, input: { amountCents: number; method: PaymentMethod; reference?: string; branchId: string; userId: string; cashControlEnabled: boolean }) => {
  const snapshot = ensurePhase8(businessId);
  const charge = snapshot.membershipCharges.find((item) => item.id === chargeId);
  if (!charge || ['paid', 'voided'].includes(charge.status)) throw new Error('El cobro ya está pagado o anulado.');
  const pending = charge.amountCents - charge.paidCents;
  if (input.amountCents <= 0 || input.amountCents > pending) throw new Error('El importe no corresponde al saldo pendiente.');
  const openSession = getOpenCashSession(businessId, input.branchId);
  if (input.method === 'cash' && input.cashControlEnabled && !openSession) throw new Error('Abre una caja antes de cobrar en efectivo.');
  if (openSession) registerCashMovement(businessId, { branchId: input.branchId, cashSessionId: openSession.id, type: 'payment', method: input.method, amountCents: input.amountCents, direction: 'in', reference: charge.folio, notes: 'Cobro de membresía', createdBy: input.userId });
  let updated!: MembershipCharge;
  updateBusiness(businessId, (current) => {
    const paidCents = charge.paidCents + input.amountCents;
    updated = { ...charge, paidCents, status: paidCents >= charge.amountCents ? 'paid' : charge.status, paymentMethod: input.method, paymentReference: input.reference?.trim(), paidAt: paidCents >= charge.amountCents ? nowIso() : undefined };
    const memberships = current.memberships.map((membership) => membership.id === charge.membershipId && updated.status === 'paid' && membership.status === 'past-due' ? { ...membership, status: 'active' as const, updatedAt: nowIso() } : membership);
    return { ...current, membershipCharges: current.membershipCharges.map((item) => item.id === chargeId ? updated : item), memberships };
  });
  recordAudit(businessId, { branchId: input.branchId, userId: input.userId, entityType: 'membership', entityId: charge.membershipId, action: 'membership.payment', summary: `${charge.folio} recibió un pago.`, metadata: { amountCents: input.amountCents, method: input.method } });
  return updated;
};

export const redeemMembershipBenefit = (businessId: string, membershipId: string, input: { benefitId: string; quantity: number; branchId: string; notes?: string; userId: string }) => {
  const snapshot = ensurePhase8(businessId);
  const membership = snapshot.memberships.find((item) => item.id === membershipId);
  if (!membership || membership.status !== 'active') throw new Error('La membresía no está activa.');
  const plan = snapshot.membershipPlans.find((item) => item.id === membership.planId);
  const benefit = plan?.benefits.find((item) => item.id === input.benefitId);
  if (!plan || !benefit) throw new Error('No se encontró el beneficio.');
  const remaining = membership.remainingBenefits[benefit.id] ?? 0;
  if (!Number.isInteger(input.quantity) || input.quantity <= 0 || input.quantity > remaining) throw new Error('La cantidad supera el beneficio disponible.');
  const currentCharge = snapshot.membershipCharges.find((charge) => charge.membershipId === membership.id && charge.periodStart === membership.currentPeriodStart);
  if (currentCharge && currentCharge.status !== 'paid') throw new Error('El periodo actual necesita estar pagado antes de usar beneficios.');
  if (benefit.productId) {
    const catalog = getCatalog(businessId);
    const product = catalog?.products.find((item) => item.id === benefit.productId);
    if (product?.trackInventory) applyInventoryBatch(businessId, [{ productId: benefit.productId, branchId: input.branchId, onHandDelta: -input.quantity, type: 'fulfillment', reason: `Beneficio ${plan.name}`, reference: membership.id, createdBy: input.userId }]);
  }
  let fulfillment!: MembershipFulfillment;
  updateBusiness(businessId, (current) => {
    fulfillment = { id: id('mful'), folio: `BEN-${String(current.membershipFulfillments.length + 1).padStart(6, '0')}`, businessId, membershipId, customerId: membership.customerId, benefitId: benefit.id, quantity: input.quantity, branchId: input.branchId, notes: input.notes?.trim() ?? '', deliveredBy: input.userId, deliveredAt: nowIso() };
    return { ...current, memberships: current.memberships.map((item) => item.id === membershipId ? { ...item, remainingBenefits: { ...item.remainingBenefits, [benefit.id]: remaining - input.quantity }, updatedAt: nowIso() } : item), membershipFulfillments: [fulfillment, ...current.membershipFulfillments] };
  });
  recordAudit(businessId, { branchId: input.branchId, userId: input.userId, entityType: 'membership', entityId: membershipId, action: 'membership.benefit.redeemed', summary: `${benefit.label}: ${input.quantity} entregado.`, metadata: { benefitId: benefit.id, quantity: input.quantity } });
  notifyCatalogChanged();
  return fulfillment;
};

export const setCustomerMembershipStatus = (businessId: string, membershipId: string, status: CustomerMembership['status'], userId: string) => {
  let updated!: CustomerMembership;
  updateBusiness(businessId, (snapshot) => {
    const membership = snapshot.memberships.find((item) => item.id === membershipId);
    if (!membership) throw new Error('No se encontró la membresía.');
    updated = { ...membership, status, cancelledAt: status === 'cancelled' ? nowIso() : membership.cancelledAt, updatedAt: nowIso() };
    return { ...snapshot, memberships: snapshot.memberships.map((item) => item.id === membershipId ? updated : item) };
  });
  recordAudit(businessId, { userId, entityType: 'membership', entityId: membershipId, action: `membership.${status}`, summary: `Membresía marcada como ${status}.`, metadata: { status } });
  return updated;
};
